// Building index — one source of truth keyed by building_id.
//
// The area load already holds everything a building needs to OPEN: the map's
// Point features carry the centroid + aggregate counts, and `results` carry the
// listings. Joining them into a `Map<building_id, entry>` lets every surface
// (preview card, panel, building view) read the SAME in-memory data instead of
// re-fetching `/building-page` — which returns a stale doc whose centroid can be
// an empty `{}` (the bug that left the building-view hero map blank).
//
// Polygon footprints are NOT in the aggregated features (those are Points); the
// map resolves a polygon only when a building is clicked/hovered. So geometry is
// a two-tier truth: `centroid` covers every building cheaply, `geometry` upgrades
// the ones the map has rendered. The resolved polygons live in `GeometryCache`
// (a ref that survives index rebuilds) and are read on demand.
import { BuildingWithConvertedAddresses } from '../../utils/types';
import { SearchResult } from '../../ExploreListings/types';
import { OffMarketBuildingProperties } from './aggregateBuildingFeatures';

export interface BuildingIndexCounts {
  sellers: number;
  buyers: number;
  likes: number;
  comments: number;
}

export interface BuildingPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface BuildingIndexEntry {
  buildingId: string;
  centroid: { lat: number; lng: number } | null;
  geometry: BuildingPolygon | null;
  listings: SearchResult[];
  counts: BuildingIndexCounts;
  isNew: boolean;
  address?: SearchResult['address'];
}

export type BuildingIndex = Map<string, BuildingIndexEntry>;

// Polygons the map has resolved, persisted across index rebuilds (a useRef).
export type GeometryCache = Map<string, BuildingPolygon>;

const EMPTY_POLYGON: BuildingPolygon = { type: 'Polygon', coordinates: [] };

function centroidFromFeature(
  feature: GeoJSON.Feature,
): { lat: number; lng: number } | null {
  const geom = feature.geometry as GeoJSON.Point | null;
  const coords = geom?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    return { lat: coords[1], lng: coords[0] };
  }
  return null;
}

/**
 * Build the building_id → data index from the area's in-memory load.
 * `geometryCache` seeds each entry with any polygon the map resolved earlier.
 */
export function buildBuildingIndex(
  results: SearchResult[],
  featureCollection: GeoJSON.FeatureCollection | null | undefined,
  geometryCache?: GeometryCache,
): BuildingIndex {
  const index: BuildingIndex = new Map();

  // 1. Seed from the aggregated building Point features (centroid + counts).
  featureCollection?.features.forEach((feature) => {
    const props = feature.properties as OffMarketBuildingProperties | null;
    const id = props?.building_id;
    if (!id) return;
    index.set(id, {
      buildingId: id,
      centroid: centroidFromFeature(feature),
      geometry: geometryCache?.get(id) ?? null,
      listings: [],
      counts: {
        sellers: props?.could_sell_count ?? 0,
        buyers: props?.could_buy_count ?? 0,
        likes: props?.like_count ?? 0,
        comments: props?.commentsCount ?? 0,
      },
      isNew: props?.is_new ?? false,
    });
  });

  // 2. Attach listing results grouped by building_id. A building may have
  //    listings without a feature (or vice-versa) — create the entry on demand.
  results.forEach((listing) => {
    const id = listing.building_id;
    if (!id) return;
    let entry = index.get(id);
    if (!entry) {
      const coord = listing.address?.coordinates;
      entry = {
        buildingId: id,
        centroid: coord ? { lat: coord.lat, lng: coord.lng } : null,
        geometry: geometryCache?.get(id) ?? null,
        listings: [],
        counts: { sellers: 0, buyers: 0, likes: 0, comments: 0 },
        isNew: false,
      };
      index.set(id, entry);
    }
    entry.listings.push(listing);
    if (!entry.address) entry.address = listing.address;
  });

  return index;
}

/**
 * Construct the core building object the building view needs to OPEN — id,
 * address, centroid, geometry. Heavy sections (price estimate, street view,
 * comments, location map) self-fetch by id/address, so this skeleton is all the
 * open needs; no `/building-page` round-trip. Reads the LIVE geometry cache so a
 * polygon resolved after the index was memoised still comes through.
 */
export function buildingFromIndexEntry(
  entry: BuildingIndexEntry,
  geometryCache?: GeometryCache,
): BuildingWithConvertedAddresses {
  const addr = entry.address;
  const fallbackCoord = entry.centroid ?? { lat: 0, lng: 0 };
  const addresses = addr
    ? [
        {
          street: addr.street,
          houseNumber: addr.houseNumber,
          city: addr.city,
          country: addr.country,
          postcode: '',
          region: '',
          coordinates: addr.coordinates ?? fallbackCoord,
        },
      ]
    : [];
  const geometry =
    geometryCache?.get(entry.buildingId) ?? entry.geometry ?? EMPTY_POLYGON;
  return {
    id: entry.buildingId,
    centroid: entry.centroid ?? addr?.coordinates ?? { lat: 0, lng: 0 },
    addresses,
    geometry,
    properties: { name: '', addresses },
    pois: [],
  } as unknown as BuildingWithConvertedAddresses;
}
